import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Instagram,
  Music,
  ArrowRight,
  Check,
  Mail,
  Ticket,
  Sparkles,
  ListMusic,
  X,
  MessageSquare,
  Star,
} from 'lucide-react';
import Nav from '../components/Nav';
import { songs, specialRequests } from '../data/songList';

// ────────────────────────────────────────────────────────────────────────
// SITE CONFIG — edit the URLs / dates here to update the page.
// No rebuild logic needed; the route (/live) stays permanent so any
// printed QR code that points at /live will always work.
// ────────────────────────────────────────────────────────────────────────
const SITE_CONFIG = {
  brand_tag: 'MODERN ROMANTIC SOUL',
  brand_sentence:
    'Songs for people who want love to feel elegant, dangerous, and honest.',
  featured_release: {
    title: 'ALL TO MYSELF',
    blurb:
      'An intimate Dionlovelle record built around desire, restraint, and the moment attraction becomes impossible to hide.',
    spotify_url: 'https://open.spotify.com/album/0fl1xqfuN53rQMdwOP3vbE',
    spotify_embed_id: '0fl1xqfuN53rQMdwOP3vbE',
    cover_image: '/dionlovelle-hero.jpg',
  },
  instagram_url: 'https://www.instagram.com/dionlovelle',
  hero_image: '/dionlovelle-hero.jpg',
  shows: [
    {
      id: 'breaking-sound',
      venue: 'BREAKING SOUND',
      date_display: 'SEPTEMBER 24, 2026',
      location: "Teddy's Speakeasy · Los Angeles",
      ticket_url:
        'https://www.tixr.com/groups/breakingsoundla/events/breaking-sound-la-at-teddy-s-speakeasy-09-24-202722',
      cta: 'BREAKING SOUND TICKETS',
      event_key: 'breaking_sound_ticket_click',
    },
  ],
  bio: 'Dionlovelle is a Soul/R&B singer-songwriter, acoustic guitarist, and live-looping performer creating emotionally direct love songs through soulful vocals, fingerstyle guitar, and magnetic live performance.',
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Editorial serif style — Cormorant Garamond loaded globally in index.html
const serifStyle = { fontFamily: "'Cormorant Garamond', Georgia, 'Times New Roman', serif" };

// Analytics helper (PostHog + dataLayer + backend event ingestion)
const trackEvent = (name, props = {}) => {
  try {
    if (typeof window !== 'undefined') {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(name, props);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...props });
      // Fire-and-forget backend event ingestion for admin analytics
      const payload = {
        name,
        path: (typeof window !== 'undefined' && window.location.pathname) || undefined,
        referrer: (typeof document !== 'undefined' && document.referrer) || undefined,
        ...props,
      };
      fetch(`${API}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    /* silent */
  }
};

// Capture UTM + source/campaign params once on mount
const useUtmParams = () =>
  useMemo(() => {
    if (typeof window === 'undefined') return {};
    const p = new URLSearchParams(window.location.search);
    const pick = (k) => p.get(k) || undefined;
    return {
      source: pick('source'),
      campaign: pick('campaign'),
      utm_source: pick('utm_source'),
      utm_medium: pick('utm_medium'),
      utm_campaign: pick('utm_campaign'),
      utm_content: pick('utm_content'),
    };
  }, []);

const LiveQRPage = () => {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const [songListOpen, setSongListOpen] = useState(false);
  const utmParams = useUtmParams();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent('live_page_view', utmParams);
  }, [utmParams]);

  // Lock body scroll while the Song List modal is open
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    if (songListOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = prev || '';
    }
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [songListOpen]);

  // Close modal with Escape key
  useEffect(() => {
    if (!songListOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSongListOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [songListOpen]);

  const openSongList = () => {
    setSongListOpen(true);
    trackEvent('song_list_open', utmParams);
  };
  const closeSongList = () => setSongListOpen(false);

  const handleFormFocus = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackEvent('lead_form_start', {});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      setError('Please add your name and email.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch(`${API}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim(),
          ...utmParams,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }
      setEmailSent(Boolean(data.email_sent));
      setSuccess(true);
      trackEvent('lead_submit', {
        is_new: data.is_new,
        email_sent: Boolean(data.email_sent),
      });
    } catch (err) {
      setError(err.message || 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTrackedClick = (eventName) => () => trackEvent(eventName, utmParams);

  return (
    <div
      className="min-h-screen text-[#F5EDE0] relative overflow-hidden"
      style={{ background: 'radial-gradient(120% 80% at 50% 0%, #16223A 0%, #0B0F1A 55%, #06070C 100%)' }}
      data-testid="live-qr-page"
    >
      {/* Shared Nav */}
      <Nav />

      {/* Grain / vignette overlay */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(70% 50% at 50% 0%, rgba(212,175,55,0.08), transparent 60%), radial-gradient(80% 60% at 50% 100%, rgba(124,31,31,0.10), transparent 60%)",
        }}
      />

      <main className="relative z-10 max-w-lg mx-auto px-5 sm:px-8 pt-24 sm:pt-28 pb-16">
        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="text-center" data-testid="hero-section">
          <p
            className="text-[10px] sm:text-xs tracking-[0.42em] text-[#D4AF37] mb-5 font-medium"
            data-testid="brand-tag"
          >
            ✦ {SITE_CONFIG.brand_tag} ✦
          </p>

          <div className="relative w-40 h-40 sm:w-48 sm:h-48 mx-auto mb-6">
            <div
              className="absolute inset-0 rounded-full blur-2xl"
              style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)' }}
              aria-hidden="true"
            />
            <img
              src={SITE_CONFIG.hero_image}
              alt="Dionlovelle"
              className="relative w-full h-full rounded-full border border-[#D4AF37]/40 shadow-2xl"
              style={{ objectFit: 'cover', objectPosition: 'center 15%' }}
              data-testid="hero-image"
            />
          </div>

          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold leading-none tracking-wide mb-2"
            style={serifStyle}
            data-testid="hero-name"
          >
            DIONLOVELLE
          </h1>

          <div className="flex items-center justify-center gap-3 my-4" aria-hidden="true">
            <span className="h-px w-8 bg-[#D4AF37]/60" />
            <span className="text-[10px] tracking-[0.4em] text-[#D4AF37]">FEATURED RELEASE</span>
            <span className="h-px w-8 bg-[#D4AF37]/60" />
          </div>

          <p
            className="text-2xl sm:text-3xl italic font-normal tracking-wide mb-5 text-[#F5EDE0]"
            style={serifStyle}
            data-testid="featured-release"
          >
            {SITE_CONFIG.featured_release.title}
          </p>

          <p className="text-sm sm:text-base text-[#E5DFD3]/80 italic max-w-md mx-auto leading-relaxed mb-8" data-testid="brand-sentence">
            &ldquo;{SITE_CONFIG.brand_sentence}&rdquo;
          </p>
        </section>

        {/* ── Lead form OR success state ───────────────────────────────── */}
        {!success ? (
          <section
            className="mb-14 rounded-3xl p-6 sm:p-8 border border-[#D4AF37]/25 shadow-2xl"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.015))', backdropFilter: 'blur(6px)' }}
            data-testid="lead-form-section"
          >
            <h2
              className="text-2xl sm:text-3xl font-semibold text-center leading-tight mb-2"
              style={serifStyle}
              data-testid="lead-form-headline"
            >
              Get the song<span className="text-[#D4AF37]"> +</span> upcoming LA shows.
            </h2>
            <p className="text-center text-xs sm:text-sm text-[#E5DFD3]/70 mb-6">
              Instantly. Straight to your inbox.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="lead-form">
              <div>
                <label htmlFor="first_name" className="sr-only">First name</label>
                <input
                  id="first_name"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onFocus={handleFormFocus}
                  placeholder="First name"
                  className="w-full px-5 py-4 rounded-full bg-black/40 border border-[#F5EDE0]/15 text-[#F5EDE0] placeholder:text-[#F5EDE0]/40 focus:outline-none focus:border-[#D4AF37] focus:bg-black/60 transition-all text-base"
                  data-testid="lead-first-name"
                />
              </div>
              <div>
                <label htmlFor="email" className="sr-only">Email address</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={handleFormFocus}
                  placeholder="Email address"
                  className="w-full px-5 py-4 rounded-full bg-black/40 border border-[#F5EDE0]/15 text-[#F5EDE0] placeholder:text-[#F5EDE0]/40 focus:outline-none focus:border-[#D4AF37] focus:bg-black/60 transition-all text-base"
                  data-testid="lead-email"
                />
              </div>

              {error && (
                <p className="text-sm text-red-300 text-center" data-testid="lead-form-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-full font-bold text-[#0B0F1A] text-sm sm:text-base tracking-[0.18em] transition-all hover:brightness-110 active:scale-[0.99] disabled:opacity-70"
                style={{ background: 'linear-gradient(180deg, #E7C56A, #C1922B)', boxShadow: '0 12px 34px -12px rgba(212,175,55,0.55)' }}
                data-testid="lead-submit-button"
              >
                {submitting ? 'SENDING…' : (
                  <>
                    GET THE SONG + SHOW DATES
                    <ArrowRight size={18} strokeWidth={2.5} />
                  </>
                )}
              </button>
            </form>

            <p className="mt-5 text-center text-[11px] sm:text-xs text-[#E5DFD3]/60 leading-relaxed">
              New music, LA performances, and occasional private releases.<br />No spam. Unsubscribe anytime.
            </p>
          </section>
        ) : (
          <SuccessState
            emailSent={emailSent}
            firstName={firstName}
            onSpotifyClick={handleTrackedClick('all_to_myself_click')}
            onBreakingSoundClick={handleTrackedClick('breaking_sound_ticket_click')}
          />
        )}

        {/* ── Featured music ───────────────────────────────────────────── */}
        <section className="mb-14" data-testid="featured-music-section">
          <SectionHeading>Start with All To Myself</SectionHeading>

          <div className="mt-6 rounded-3xl overflow-hidden border border-[#D4AF37]/20 shadow-2xl" style={{ background: 'rgba(255,255,255,0.02)' }}>
            <div className="p-4 sm:p-5">
              <iframe
                data-testid="featured-spotify-embed"
                src={`https://open.spotify.com/embed/album/${SITE_CONFIG.featured_release.spotify_embed_id}?utm_source=generator&theme=0`}
                width="100%"
                height="80"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="Dionlovelle — All To Myself"
                style={{ borderRadius: 12, display: 'block' }}
              />
            </div>
            <div className="px-5 sm:px-6 pb-6">
              <p className="text-sm sm:text-base text-[#E5DFD3]/85 leading-relaxed mb-5" data-testid="featured-music-blurb">
                {SITE_CONFIG.featured_release.blurb}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={SITE_CONFIG.featured_release.spotify_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTrackedClick('all_to_myself_click')}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-bold text-[#0B0F1A] text-xs sm:text-sm tracking-[0.16em] hover:brightness-110 transition-all"
                  style={{ background: 'linear-gradient(180deg, #E7C56A, #C1922B)' }}
                  data-testid="listen-spotify-button"
                >
                  <Music size={16} />
                  LISTEN ON SPOTIFY
                </a>
                <a
                  href={SITE_CONFIG.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTrackedClick('instagram_click')}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-semibold text-[#F5EDE0] text-xs sm:text-sm tracking-[0.16em] border border-[#F5EDE0]/25 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  data-testid="instagram-button"
                >
                  <Instagram size={16} />
                  FOLLOW ON INSTAGRAM
                </a>
              </div>
              <button
                type="button"
                onClick={openSongList}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-semibold text-[#F5EDE0] text-xs sm:text-sm tracking-[0.16em] border border-[#F5EDE0]/25 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                data-testid="song-list-button"
              >
                <ListMusic size={16} />
                SONG LIST
              </button>
            </div>
          </div>
        </section>

        {/* ── Upcoming shows ───────────────────────────────────────────── */}
        <section className="mb-14" data-testid="upcoming-shows-section">
          <SectionHeading>Upcoming LA Shows</SectionHeading>

          <div className="mt-6 space-y-4">
            {SITE_CONFIG.shows.map((show) => (
              <div
                key={show.id}
                className="rounded-3xl p-6 sm:p-7 border border-[#D4AF37]/20 shadow-lg"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.01))' }}
                data-testid={`show-card-${show.id}`}
              >
                <p className="text-[10px] sm:text-xs tracking-[0.35em] text-[#D4AF37] mb-2">LIVE IN LA</p>
                <h3 className="text-2xl sm:text-3xl font-bold tracking-wide" style={serifStyle} data-testid={`show-venue-${show.id}`}>
                  {show.venue}
                </h3>
                <p className="text-sm sm:text-base text-[#F5EDE0]/90 mt-2 mb-1" data-testid={`show-date-${show.id}`}>
                  {show.date_display}
                </p>
                <p className="text-xs sm:text-sm text-[#E5DFD3]/60 mb-5" data-testid={`show-location-${show.id}`}>
                  {show.location}
                </p>
                <a
                  href={show.ticket_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTrackedClick(show.event_key)}
                  className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-[#0B0F1A] text-xs sm:text-sm tracking-[0.16em] hover:brightness-110 transition-all"
                  style={{ background: 'linear-gradient(180deg, #E7C56A, #C1922B)' }}
                  data-testid={`show-cta-${show.id}`}
                >
                  <Ticket size={16} />
                  {show.cta}
                </a>
              </div>
            ))}
          </div>
        </section>

        {/* ── Live proof / bio ─────────────────────────────────────────── */}
        <section className="mb-14 text-center" data-testid="live-proof-section">
          <h2 className="text-2xl sm:text-3xl font-semibold leading-snug mb-5" style={serifStyle} data-testid="live-proof-heading">
            Intimate soul. Magnetic presence.<br />Unforgettable voice.
          </h2>
          <p className="text-sm sm:text-base text-[#E5DFD3]/85 leading-relaxed max-w-md mx-auto" data-testid="bio-text">
            {SITE_CONFIG.bio}
          </p>
        </section>

        {/* ── Professional footer (secondary) ──────────────────────────── */}
        <section className="pt-6 border-t border-[#F5EDE0]/10 text-center" data-testid="pro-footer-section">
          <p className="text-[10px] sm:text-xs tracking-[0.4em] text-[#E5DFD3]/50 mb-4">
            BOOKING AND INDUSTRY
          </p>
          <div className="flex items-center justify-center gap-6 text-xs sm:text-sm">
            <Link
              to="/booking"
              onClick={handleTrackedClick('booking_click')}
              className="text-[#E5DFD3]/70 hover:text-[#D4AF37] transition-colors border-b border-dotted border-transparent hover:border-[#D4AF37] pb-0.5"
              data-testid="book-dionlovelle-link"
            >
              Book Dionlovelle
            </Link>
            <span className="text-[#E5DFD3]/25" aria-hidden="true">·</span>
            <Link
              to="/press-kit"
              onClick={handleTrackedClick('epk_click')}
              className="text-[#E5DFD3]/70 hover:text-[#D4AF37] transition-colors border-b border-dotted border-transparent hover:border-[#D4AF37] pb-0.5"
              data-testid="view-epk-link"
            >
              View the EPK
            </Link>
          </div>
          <p className="mt-6 text-[10px] text-[#E5DFD3]/30">© 2026 Dionlovelle</p>
        </section>
      </main>

      {songListOpen && (
        <SongListModal onClose={closeSongList} />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
//  Song List Modal — matches the /live page visual language
// ────────────────────────────────────────────────────────────────────────
const SongListModal = ({ onClose }) => {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Song List"
      data-testid="song-list-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
        data-testid="song-list-modal-backdrop"
      />

      {/* Panel */}
      <div
        className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] flex flex-col rounded-t-3xl sm:rounded-3xl border border-[#D4AF37]/25 shadow-2xl overflow-hidden animate-fade-in"
        style={{
          background:
            'radial-gradient(120% 80% at 50% 0%, #16223A 0%, #0B0F1A 55%, #06070C 100%)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 pt-5 pb-4 border-b border-[#F5EDE0]/10 flex-shrink-0">
          <div>
            <p className="text-[10px] tracking-[0.4em] text-[#D4AF37] mb-1">
              ✦ LIVE REQUESTS ✦
            </p>
            <h3
              className="text-2xl sm:text-3xl font-semibold tracking-wide"
              style={serifStyle}
              data-testid="song-list-modal-title"
            >
              Song List
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close song list"
            className="w-10 h-10 rounded-full border border-[#F5EDE0]/20 text-[#F5EDE0] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors flex items-center justify-center flex-shrink-0"
            data-testid="song-list-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        <div
          className="overflow-y-auto overscroll-contain px-5 sm:px-6 py-5"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="song-list-modal-body"
        >
          {/* Pinned / special requests */}
          <div className="space-y-2.5 mb-5" data-testid="song-list-modal-specials">
            {specialRequests.map((req, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-2xl p-3.5 border border-[#D4AF37]/40"
                style={{
                  background:
                    'linear-gradient(90deg, rgba(212,175,55,0.10), rgba(124,31,31,0.10))',
                }}
              >
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/50 text-[#D4AF37]">
                  {i === 0 ? <Sparkles size={18} /> : <Star size={18} />}
                </div>
                <p className="flex-1 text-sm sm:text-base font-semibold text-[#F5EDE0] leading-snug">
                  {req}
                </p>
              </div>
            ))}
          </div>

          <p className="text-[10px] tracking-[0.35em] text-[#D4AF37] mb-3">
            {songs.length} SONGS
          </p>

          {/* Song list */}
          <ol className="space-y-2" data-testid="song-list-modal-list">
            {songs.map((song, i) => (
              <li
                key={i}
                className="flex items-center gap-3 rounded-xl px-3.5 py-3 border border-[#F5EDE0]/10 bg-white/[0.03]"
                data-testid={`song-list-modal-item-${i + 1}`}
              >
                <span
                  className="w-8 h-8 flex-shrink-0 rounded-lg border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-bold flex items-center justify-center tabular-nums"
                  style={{ background: 'rgba(212,175,55,0.08)' }}
                >
                  {i + 1}
                </span>
                <span className="flex-1 text-sm sm:text-base text-[#F5EDE0]/90 leading-snug">
                  {song}
                </span>
              </li>
            ))}
          </ol>

          <p className="mt-6 text-center text-[11px] sm:text-xs text-[#E5DFD3]/60 italic leading-relaxed">
            Don&rsquo;t see it? Shout it out from the crowd — I&rsquo;ll learn it in 2 minutes.
          </p>
        </div>

        {/* Footer close (mobile-friendly) */}
        <div className="px-5 sm:px-6 py-4 border-t border-[#F5EDE0]/10 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-[#F5EDE0] text-xs sm:text-sm tracking-[0.18em] border border-[#F5EDE0]/25 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
            data-testid="song-list-modal-close-footer"
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
//  Sub-components
// ────────────────────────────────────────────────────────────────────────

const SectionHeading = ({ children }) => (
  <div className="text-center">
    <div className="flex items-center justify-center gap-3 mb-4" aria-hidden="true">
      <span className="h-px w-10 bg-[#D4AF37]/50" />
      <Sparkles size={12} className="text-[#D4AF37]" />
      <span className="h-px w-10 bg-[#D4AF37]/50" />
    </div>
    <h2
      className="text-2xl sm:text-3xl font-semibold tracking-wide uppercase"
      style={serifStyle}
    >
      {children}
    </h2>
  </div>
);

const SuccessState = ({ emailSent, firstName, onSpotifyClick, onBreakingSoundClick }) => {
  const displayName = firstName ? firstName.trim().split(/\s+/)[0] : '';
  return (
    <section
      className="mb-14 rounded-3xl p-7 sm:p-9 border border-[#D4AF37]/40 shadow-2xl text-center animate-fade-in"
      style={{ background: 'linear-gradient(180deg, rgba(212,175,55,0.10), rgba(255,255,255,0.02))' }}
      data-testid="success-state"
    >
      <div
        className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center border border-[#D4AF37]/60"
        style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.25), transparent 70%)' }}
      >
        <Check size={26} className="text-[#D4AF37]" strokeWidth={3} />
      </div>

      <p className="text-[10px] sm:text-xs tracking-[0.42em] text-[#D4AF37] mb-3">✦ WELCOME ✦</p>
      <h2 className="text-4xl sm:text-5xl font-bold mb-3 tracking-wider" style={serifStyle} data-testid="success-title">
        YOU&rsquo;RE IN{displayName ? `, ${displayName.toUpperCase()}` : ''}.
      </h2>
      <p className="text-sm sm:text-base text-[#E5DFD3]/85 leading-relaxed mb-2 italic" style={serifStyle}>
        Start with &ldquo;All To Myself,&rdquo; then choose the show where you want to hear it live.
      </p>
      {emailSent ? (
        <p className="text-xs text-[#E5DFD3]/60 mb-6" data-testid="success-email-note">
          Check your inbox — the song and show links are on their way.
        </p>
      ) : (
        <p className="text-xs text-[#E5DFD3]/60 mb-6" data-testid="success-no-email-note">
          Your buttons are ready right here 👇
        </p>
      )}

      <div className="space-y-3">
        <a
          href={SITE_CONFIG.featured_release.spotify_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onSpotifyClick}
          className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full font-bold text-[#0B0F1A] text-xs sm:text-sm tracking-[0.18em] hover:brightness-110 transition-all"
          style={{ background: 'linear-gradient(180deg, #E7C56A, #C1922B)', boxShadow: '0 10px 30px -12px rgba(212,175,55,0.55)' }}
          data-testid="success-listen-button"
        >
          <Music size={16} />
          LISTEN TO ALL TO MYSELF
        </a>
        <a
          href={SITE_CONFIG.shows[0].ticket_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onBreakingSoundClick}
          className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full font-semibold text-[#F5EDE0] text-xs sm:text-sm tracking-[0.18em] border border-[#F5EDE0]/30 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
          data-testid="success-breaking-sound-button"
        >
          <Ticket size={16} />
          BREAKING SOUND — SEPTEMBER 24
        </a>
      </div>
    </section>
  );
};

export default LiveQRPage;
